/**
 * Shared utilities for sales data transformation
 * Ensures consistency between SalesPage and SalesReports
 */
import { formatBrandMoney, getBrandLocale } from './brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';

/**
 * Format amount for display using brand currency (SAR / ر.س for Saudi).
 */
export const formatAmount = (amount) => formatBrandMoney(amount);

/**
 * Format date for display using brand locale (ar-SA for Saudi).
 */
export const formatDate = (dateStr, language) => {
  const locale = getBrandLocale(language);
  if (!dateStr) return new Date().toLocaleDateString(locale);
  const date = new Date(dateStr);
  return date.toLocaleDateString(locale);
};

/**
 * Get entity name from customer object
 */
export const getEntityName = (entity) => {
  if (!entity) return isSaudiBrand() ? 'عميل غير معروف' : 'Client inconnu';

  if (entity.type === 'company' && entity.companyName) {
    return entity.companyName;
  }

  if (entity.firstName && entity.lastName) {
    return `${entity.firstName} ${entity.lastName}`;
  }

  if (entity.name) {
    return entity.name;
  }

  return isSaudiBrand() ? 'عميل غير معروف' : 'Client inconnu';
};

/**
 * Transform raw sales data to normalized display format
 * This is the SINGLE SOURCE OF TRUTH for sales data transformation
 */
export const transformSalesData = (rawSales, customers = []) => {
  if (!rawSales || !Array.isArray(rawSales)) {
    return [];
  }

  const transformedSales = rawSales.map((sale) => {
    const customer = customers.find((c) => c.id === sale.customerId);

    let amount = 0;
    if (Array.isArray(sale.items) && sale.items.length > 0) {
      amount = sale.items.reduce((sum, item) => {
        const itemTotal = Number(item.total) || (Number(item.quantity || 0) * Number(item.price || 0));
        return sum + itemTotal;
      }, 0);
    } else {
      amount = Number(sale.total || sale.totalAmount || sale.amount || 0);
    }

    const transformedSale = {
      id: sale.id,
      invoiceNumber: sale.invoiceNumber || sale.id,
      customer: sale.customerName || getEntityName(customer),
      customerId: sale.customerId,
      customerName: sale.customerName || getEntityName(customer),
      date: formatDate(sale.date || sale.createdAt),
      rawDate: sale.date || sale.createdAt,
      amount: formatAmount(amount),
      rawAmount: amount,
      status: sale.status || 'completed',
      type: sale.type || 'retail',
      paymentMethod: sale.paymentMethod || 'cash',
      items: sale.items || [],
      notes: sale.notes || '',
      __raw: sale,
    };

    return transformedSale;
  }).sort((a, b) => {
    const dateA = new Date(a.rawDate || Date.now());
    const dateB = new Date(b.rawDate || Date.now());
    return dateB - dateA;
  });

  return transformedSales;
};

/**
 * Calculate sales summary statistics
 */
export const calculateSalesStats = (transformedSales) => {
  if (!transformedSales || !Array.isArray(transformedSales)) {
    return {
      totalSales: 0,
      totalAmount: 0,
      averageOrderValue: 0,
      completedSales: 0,
      pendingSales: 0,
      totalItems: 0,
    };
  }

  const totalAmount = transformedSales.reduce((sum, sale) => sum + sale.rawAmount, 0);
  const completedSales = transformedSales.filter((sale) => sale.status === 'completed').length;
  const pendingSales = transformedSales.filter((sale) => sale.status === 'pending').length;
  const totalItems = transformedSales.reduce((sum, sale) => sum + (sale.items?.length || 0), 0);

  return {
    totalSales: transformedSales.length,
    totalAmount,
    averageOrderValue: transformedSales.length > 0 ? totalAmount / transformedSales.length : 0,
    completedSales,
    pendingSales,
    totalItems,
  };
};

/**
 * Group sales by period for charts
 */
export const groupSalesByPeriod = (transformedSales, periodType = 'month') => {
  if (!transformedSales || !Array.isArray(transformedSales)) return [];

  const periods = {};

  transformedSales.forEach((sale) => {
    const date = new Date(sale.rawDate || Date.now());
    let periodKey;

    switch (periodType) {
      case 'day':
        periodKey = date.toISOString().split('T')[0];
        break;
      case 'week': {
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        periodKey = weekStart.toISOString().split('T')[0];
        break;
      }
      case 'month':
        periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        break;
      case 'quarter':
        periodKey = `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`;
        break;
      case 'semester':
        periodKey = `${date.getFullYear()}-S${date.getMonth() < 6 ? 1 : 2}`;
        break;
      case 'year':
        periodKey = String(date.getFullYear());
        break;
      default:
        periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }

    if (!periods[periodKey]) {
      periods[periodKey] = {
        period: periodKey,
        sales: [],
        totalAmount: 0,
        totalSales: 0,
      };
    }

    periods[periodKey].sales.push(sale);
    periods[periodKey].totalAmount += sale.rawAmount;
    periods[periodKey].totalSales += 1;
  });

  return Object.values(periods).sort((a, b) => a.period.localeCompare(b.period));
};

/**
 * Group sales by customer
 */
export const groupSalesByCustomer = (transformedSales) => {
  if (!transformedSales || !Array.isArray(transformedSales)) return [];

  const customers = {};
  const unknownLabel = isSaudiBrand() ? 'عميل غير معروف' : 'Client inconnu';

  transformedSales.forEach((sale) => {
    const customerName = sale.customerName || unknownLabel;

    if (!customers[customerName]) {
      customers[customerName] = {
        customer: customerName,
        sales: [],
        totalAmount: 0,
        totalSales: 0,
        totalItems: 0,
        averageOrderValue: 0,
      };
    }

    customers[customerName].sales.push(sale);
    customers[customerName].totalAmount += sale.rawAmount;
    customers[customerName].totalSales += 1;
    customers[customerName].totalItems += sale.items?.length || 0;
  });

  Object.values(customers).forEach((customer) => {
    customer.averageOrderValue = customer.totalSales > 0
      ? customer.totalAmount / customer.totalSales
      : 0;
  });

  return Object.values(customers).sort((a, b) => b.totalAmount - a.totalAmount);
};

/**
 * Group sales by product
 */
export const groupSalesByProduct = (transformedSales, products = []) => {
  if (!transformedSales || !Array.isArray(transformedSales)) return [];

  const productSales = {};
  const unknownProduct = isSaudiBrand() ? 'منتج غير معروف' : 'Produit inconnu';

  transformedSales.forEach((sale) => {
    if (sale.items && Array.isArray(sale.items)) {
      sale.items.forEach((item) => {
        const productId = item.id || item.productId;
        const product = products.find((p) => p.id === productId);
        const productName = item.name || product?.name || unknownProduct;

        if (!productSales[productName]) {
          productSales[productName] = {
            product: productName,
            category: product?.category || (isSaudiBrand() ? 'أخرى' : 'Autre'),
            quantitySold: 0,
            totalAmount: 0,
            averagePrice: 0,
            salesCount: 0,
          };
        }

        const quantity = Number(item.quantity || 0);
        const price = Number(item.price || 0);
        const itemTotal = Number(item.total || (quantity * price));

        productSales[productName].quantitySold += quantity;
        productSales[productName].totalAmount += itemTotal;
        productSales[productName].salesCount += 1;
        productSales[productName].averagePrice =
          productSales[productName].quantitySold > 0
            ? productSales[productName].totalAmount / productSales[productName].quantitySold
            : 0;
      });
    }
  });

  return Object.values(productSales).sort((a, b) => b.totalAmount - a.totalAmount);
};
